// Minimal i18n based on navigator.language
// Falls back to English when locale is not supported.

const dict = {
  en: {
    live: "Live · Real",
    off: "Off",
    timerOff: "Timer off",
    timer: (s) => `Timer: ${s}s`,
    aspect: (label) => `Ratio: ${label}`,
    flashOn: "Flash: on",
    flashOff: "Flash: off",
    previewMirrored: "Preview mirrored · Real photo captured",
    previewReal: "Preview · Real image",
    savedToGallery: "Saved to gallery",
    realSelfie: "Real Selfie",
    on: "On",
    // Captured screen
    retake: "Retake",
    share: "Share",
    download: "Download",
    // Toasts
    photoSaved: "Photo saved",
    shareFail: "Sharing failed",
    shareUnsupported: "Your device doesn't support native sharing",
    photoDeleted: "Photo deleted",
    galleryCleared: "Gallery cleared",
    // Errors
    cameraUnavailable: "Camera not available",
    permissionDenied: "Camera access was denied. Allow it in your browser settings.",
    noCamera: "No camera was found.",
    cameraBusy: "Camera is in use by another app.",
    cameraGeneric: "Could not open the camera.",
    tryAgain: "Try again",
    // Gallery
    gallery: "Gallery",
    photos: (n) => (n === 1 ? "1 photo" : `${n} photos`),
    noPhotos: "No photos",
    noPhotosHint: "Captured photos will appear here. They are stored only on your device.",
    loading: "Loading…",
    deleteAll: "All",
    deleteAllConfirm: (n) => `Really delete all ${n} photos?`,
    delete: "Delete",
    done: "Done",
    // Filter panel
    adjustments: "Adjustments",
    brightness: "Brightness",
    contrast: "Contrast",
    saturation: "Saturation",
    warmth: "Warmth",
    presetOriginal: "Original",
    presetBw: "B&W",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Custom",
    // Auto-save
    autoSave: "Auto-save",
    autoSaveOn: "Auto-save: on",
    autoSaveOff: "Auto-save: off",
    autoShare: "Share",
    autoShareOn: "Auto-share: on",
    autoShareOff: "Auto-share: off",
    savedToFolder: (name) => `Saved to ${name}`,
    savedToDownloads: "Saved to Downloads as RealSelfie_…",
    pickFolderPrompt: "Choose where to save photos",
    folderPickFailed: "Could not access folder. Downloads will be used.",
    // Where-saved hint
    hintTitle: "Where do my photos go?",
    hintIosBody: "Photos save automatically as RealSelfie_*.jpg.\n\nOn iPhone:\n• Open the Files app\n• Browse → On My iPhone → Downloads\n• (or iCloud Drive → Downloads if iCloud is on)\n\nNote: iOS browsers cannot write directly to the Photos app from the web.",
    hintAndroidBody: "Photos save automatically as RealSelfie_*.jpg.\n\nOn Android:\n• Open the Files app (or My Files)\n• Internal storage → Download\n• Look for files starting with RealSelfie_",
    hintDesktopBody: "Photos save automatically as RealSelfie_*.jpg.\n\nOn this browser, you can pick a folder and the app will write photos there silently. Otherwise files land in your Downloads folder.",
    hintGalleryTip: "Tap the 3-dots menu (bottom-right) → Gallery to see all your photos inside the app — always available.",
    hintShareTip: "After taking a photo, tap Share — your system menu can save directly to Photos / Gallery.",
    hintDesktopFolderTip: "Tip: turn Auto-save ON to pick a RealSelfie folder once and save there forever.",
    gotIt: "Got it",
    whereSaved: "Where are my photos?",
    // Paywall
    paywallTitle: "Unlock Real Selfie",
    paywallSubtitle: (n) => `You've used your ${n} free photos. Get unlimited captures + everything below for one tiny payment.`,
    paywallBenefitUnlimited: "Unlimited photos forever",
    paywallBenefitFilters: "All filter presets unlocked",
    paywallBenefitAspect: "All aspect ratios & front flash",
    paywallBenefitFuture: "All future Premium features included",
    paywallCta: "Unlock for 1,99 €",
    paywallSkip: "Maybe later",
    paywallSecureNote: "Secure payment by Stripe. One-time, no subscription.",
    paymentPending: "Processing payment…",
    premiumUnlocked: "Premium unlocked!",
    paymentFailed: "Payment was not completed",
    freeLeft: (n) => (n === 1 ? "1 free shot left" : `${n} free shots left`),
    freeUsed: "Free trial used",
    premiumBadge: "Premium",
    restorePremium: "Restore Premium",
    restoreTitle: "Already paid? Restore",
    restoreDescription: "Enter the email you used at checkout. We'll unlock Premium on this device.",
    restoreEmailPlaceholder: "your@email.com",
    restoreSubmit: "Restore Premium",
    restoreSuccess: "Premium restored! 🎉",
    restoreNotFound: "We didn't find a paid account with this email.",
    restoreError: "Restore failed. Please try again.",
  },
  sk: {
    live: "Live · Reálne",
    off: "Off",
    timerOff: "Časovač vypnutý",
    timer: (s) => `Časovač: ${s}s`,
    aspect: (label) => `Pomer: ${label}`,
    flashOn: "Blesk: zapnutý",
    flashOff: "Blesk: vypnutý",
    previewMirrored: "Náhľad zrkadlený · Fotka v reálnom zobrazení",
    previewReal: "Náhľad · Reálny obraz",
    savedToGallery: "Uložené v galérii",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Znova",
    share: "Zdieľať",
    download: "Stiahnuť",
    photoSaved: "Fotka uložená",
    shareFail: "Zdieľanie sa nepodarilo",
    shareUnsupported: "Vaše zariadenie nepodporuje natívne zdieľanie",
    photoDeleted: "Fotka odstránená",
    galleryCleared: "Galéria vyčistená",
    cameraUnavailable: "Kamera nie je dostupná",
    permissionDenied: "Prístup ku kamere bol zamietnutý. Povoľte ho v nastaveniach prehliadača.",
    noCamera: "Nenašla sa žiadna kamera.",
    cameraBusy: "Kamera je obsadená inou aplikáciou.",
    cameraGeneric: "Nepodarilo sa otvoriť kameru.",
    tryAgain: "Skúsiť znova",
    gallery: "Galéria",
    photos: (n) => (n === 1 ? "1 fotka" : n >= 2 && n <= 4 ? `${n} fotky` : `${n} fotiek`),
    noPhotos: "Žiadne fotky",
    noPhotosHint: "Nasnímané fotky sa zobrazia tu. Sú uložené iba vo vašom zariadení.",
    loading: "Načítavam…",
    deleteAll: "Všetko",
    deleteAllConfirm: (n) => `Naozaj odstrániť všetkých ${n} fotiek?`,
    delete: "Zmazať",
    done: "Hotovo",
    adjustments: "Úpravy",
    brightness: "Jas",
    contrast: "Kontrast",
    saturation: "Sýtosť",
    warmth: "Teplota",
    presetOriginal: "Pôvodný",
    presetBw: "Č/B",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Vlastný",
    autoSave: "Auto-uložiť",
    autoSaveOn: "Auto-ukladanie: zapnuté",
    autoSaveOff: "Auto-ukladanie: vypnuté",
    autoShare: "Zdieľať",
    autoShareOn: "Auto-zdieľanie: zapnuté",
    autoShareOff: "Auto-zdieľanie: vypnuté",
    savedToFolder: (name) => `Uložené do ${name}`,
    savedToDownloads: "Uložené do Downloads (RealSelfie_…)",
    pickFolderPrompt: "Vyberte priečinok pre fotky",
    folderPickFailed: "Priečinok nedostupný. Použijú sa Downloads.",
    hintTitle: "Kde nájdem svoje fotky?",
    hintIosBody: "Fotky sa automaticky ukladajú ako RealSelfie_*.jpg.\n\nNa iPhone:\n• Otvorte app Súbory\n• Prehľadávať → V mojom iPhone → Downloads\n• (alebo iCloud Drive → Downloads ak používate iCloud)\n\nPoznámka: iOS prehliadače nemôžu zapisovať priamo do app Fotky z webu.",
    hintAndroidBody: "Fotky sa automaticky ukladajú ako RealSelfie_*.jpg.\n\nNa Androide:\n• Otvorte app Súbory (alebo Moje súbory)\n• Interné úložisko → Download\n• Hľadajte súbory začínajúce na RealSelfie_",
    hintDesktopBody: "Fotky sa automaticky ukladajú ako RealSelfie_*.jpg.\n\nV tomto prehliadači si môžete vybrať priečinok a apka tam bude ticho zapisovať. Inak fotky pristanú v Downloads.",
    hintGalleryTip: "Ťuknite na menu 3 bodky vpravo dole → Galéria — všetky vaše fotky sú vždy dostupné v aplikácii.",
    hintShareTip: "Po zachytení fotky ťuknite na Zdieľať — systémové menu vie uložiť priamo do Fotky / Galéria.",
    hintDesktopFolderTip: "Tip: zapnite Auto-uložiť a raz vyberte priečinok RealSelfie — odvtedy sa všetko ukladá tam.",
    gotIt: "Rozumiem",
    whereSaved: "Kde sú moje fotky?",
    paywallTitle: "Odomknite Real Selfie",
    paywallSubtitle: (n) => `Použili ste svojich ${n} fotiek zdarma. Získajte neobmedzené fotenie + všetko nižšie za jednu malú platbu.`,
    paywallBenefitUnlimited: "Neobmedzené fotky navždy",
    paywallBenefitFilters: "Všetky filter predvoľby odomknuté",
    paywallBenefitAspect: "Všetky pomery strán + front blesk",
    paywallBenefitFuture: "Všetky budúce Premium funkcie",
    paywallCta: "Odomknúť za 1,99 €",
    paywallSkip: "Možno neskôr",
    paywallSecureNote: "Bezpečná platba cez Stripe. Jednorazovo, žiadne predplatné.",
    paymentPending: "Spracovávam platbu…",
    premiumUnlocked: "Premium odomknuté!",
    paymentFailed: "Platba nebola dokončená",
    freeLeft: (n) => (n === 1 ? "Posledná fotka zdarma" : `${n} fotky zdarma zostávajú`),
    freeUsed: "Skúšobná verzia vyčerpaná",
    premiumBadge: "Premium",
    restorePremium: "Obnoviť Premium",
    restoreTitle: "Už ste platili? Obnoviť",
    restoreDescription: "Zadajte email, ktorý ste použili pri platbe. Premium sa obnoví na tomto zariadení.",
    restoreEmailPlaceholder: "vas@email.sk",
    restoreSubmit: "Obnoviť Premium",
    restoreSuccess: "Premium obnovené! 🎉",
    restoreNotFound: "S týmto emailom sme nenašli platbu.",
    restoreError: "Obnova zlyhala. Skúste znova.",
  },
  cs: {
    live: "Live · Reálné",
    off: "Off",
    timerOff: "Časovač vypnut",
    timer: (s) => `Časovač: ${s}s`,
    aspect: (label) => `Poměr: ${label}`,
    flashOn: "Blesk: zapnutý",
    flashOff: "Blesk: vypnutý",
    previewMirrored: "Náhled zrcadlený · Fotka v reálném zobrazení",
    previewReal: "Náhled · Reálný obraz",
    savedToGallery: "Uloženo v galerii",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Znovu",
    share: "Sdílet",
    download: "Stáhnout",
    photoSaved: "Fotka uložena",
    shareFail: "Sdílení se nezdařilo",
    shareUnsupported: "Vaše zařízení nepodporuje nativní sdílení",
    photoDeleted: "Fotka odstraněna",
    galleryCleared: "Galerie vyčištěna",
    cameraUnavailable: "Kamera není dostupná",
    permissionDenied: "Přístup ke kameře byl zamítnut. Povolte ho v nastavení prohlížeče.",
    noCamera: "Nenalezena žádná kamera.",
    cameraBusy: "Kamera je obsazená jinou aplikací.",
    cameraGeneric: "Nepodařilo se otevřít kameru.",
    tryAgain: "Zkusit znovu",
    gallery: "Galerie",
    photos: (n) => (n === 1 ? "1 fotka" : n >= 2 && n <= 4 ? `${n} fotky` : `${n} fotek`),
    noPhotos: "Žádné fotky",
    noPhotosHint: "Pořízené fotky se zobrazí zde. Jsou uloženy pouze ve vašem zařízení.",
    loading: "Načítám…",
    deleteAll: "Vše",
    deleteAllConfirm: (n) => `Opravdu odstranit všech ${n} fotek?`,
    delete: "Smazat",
    done: "Hotovo",
    adjustments: "Úpravy",
    brightness: "Jas",
    contrast: "Kontrast",
    saturation: "Sytost",
    warmth: "Teplota",
    presetOriginal: "Původní",
    presetBw: "Č/B",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Vlastní",
    autoSave: "Auto-ulož.",
    autoSaveOn: "Auto-ukládání: zapnuto",
    autoSaveOff: "Auto-ukládání: vypnuto",
    autoShare: "Sdílet",
    autoShareOn: "Auto-sdílení: zapnuto",
    autoShareOff: "Auto-sdílení: vypnuto",
    savedToFolder: (name) => `Uloženo do ${name}`,
    savedToDownloads: "Uloženo do Downloads (RealSelfie_…)",
    pickFolderPrompt: "Vyberte složku pro fotky",
    folderPickFailed: "Složka nedostupná. Použijí se Downloads.",
    hintTitle: "Kde najdu své fotky?",
    hintIosBody: "Fotky se automaticky ukládají jako RealSelfie_*.jpg.\n\nNa iPhone:\n• Otevřete app Soubory\n• Procházet → V mém iPhonu → Downloads\n• (nebo iCloud Drive → Downloads pokud používáte iCloud)\n\nPoznámka: iOS prohlížeče nemohou zapisovat přímo do app Fotky z webu.",
    hintAndroidBody: "Fotky se automaticky ukládají jako RealSelfie_*.jpg.\n\nNa Androidu:\n• Otevřete app Soubory (nebo Moje soubory)\n• Interní úložiště → Download\n• Hledejte soubory začínající na RealSelfie_",
    hintDesktopBody: "Fotky se automaticky ukládají jako RealSelfie_*.jpg.\n\nV tomto prohlížeči můžete vybrat složku a aplikace tam bude tiše zapisovat. Jinak fotky skončí v Downloads.",
    hintGalleryTip: "Klepněte na menu 3 tečky vpravo dole → Galerie — všechny vaše fotky jsou vždy dostupné v aplikaci.",
    hintShareTip: "Po pořízení fotky klepněte na Sdílet — systémové menu uloží přímo do Fotky / Galerie.",
    hintDesktopFolderTip: "Tip: zapněte Auto-uložit a jednou vyberte složku RealSelfie — od té doby se vše ukládá tam.",
    gotIt: "Rozumím",
    whereSaved: "Kde jsou moje fotky?",
    paywallTitle: "Odemkněte Real Selfie",
    paywallSubtitle: (n) => `Využili jste svých ${n} fotek zdarma. Získejte neomezené focení + vše níže za jednu malou platbu.`,
    paywallBenefitUnlimited: "Neomezené fotky navždy",
    paywallBenefitFilters: "Všechny filtry odemčené",
    paywallBenefitAspect: "Všechny poměry stran + přední blesk",
    paywallBenefitFuture: "Všechny budoucí Premium funkce",
    paywallCta: "Odemknout za 1,99 €",
    paywallSkip: "Možná později",
    paywallSecureNote: "Bezpečná platba přes Stripe. Jednorázově, žádné předplatné.",
    paymentPending: "Zpracovávám platbu…",
    premiumUnlocked: "Premium odemčeno!",
    paymentFailed: "Platba nebyla dokončena",
    freeLeft: (n) => (n === 1 ? "Poslední fotka zdarma" : `${n} fotek zdarma zbývá`),
    freeUsed: "Zkušební verze vyčerpána",
    premiumBadge: "Premium",
    restorePremium: "Obnovit Premium",
    restoreTitle: "Už jste platili? Obnovit",
    restoreDescription: "Zadejte e-mail, který jste použili při platbě. Premium se obnoví na tomto zařízení.",
    restoreEmailPlaceholder: "vas@email.cz",
    restoreSubmit: "Obnovit Premium",
    restoreSuccess: "Premium obnoveno! 🎉",
    restoreNotFound: "S tímto e-mailem jsme nenašli platbu.",
    restoreError: "Obnovení selhalo. Zkuste znovu.",
  },
  de: {
    live: "Live · Real",
    off: "Off",
    timerOff: "Timer aus",
    timer: (s) => `Timer: ${s}s`,
    aspect: (label) => `Format: ${label}`,
    flashOn: "Blitz: an",
    flashOff: "Blitz: aus",
    previewMirrored: "Vorschau gespiegelt · Foto im echten Bild",
    previewReal: "Vorschau · Echtes Bild",
    savedToGallery: "In Galerie gespeichert",
    realSelfie: "Real Selfie",
    on: "An",
    retake: "Wiederh.",
    share: "Teilen",
    download: "Speichern",
    photoSaved: "Foto gespeichert",
    shareFail: "Teilen fehlgeschlagen",
    shareUnsupported: "Ihr Gerät unterstützt kein natives Teilen",
    photoDeleted: "Foto gelöscht",
    galleryCleared: "Galerie geleert",
    cameraUnavailable: "Kamera nicht verfügbar",
    permissionDenied: "Kamerazugriff verweigert. Bitte in den Browsereinstellungen erlauben.",
    noCamera: "Keine Kamera gefunden.",
    cameraBusy: "Kamera wird von einer anderen App verwendet.",
    cameraGeneric: "Kamera konnte nicht geöffnet werden.",
    tryAgain: "Erneut versuchen",
    gallery: "Galerie",
    photos: (n) => (n === 1 ? "1 Foto" : `${n} Fotos`),
    noPhotos: "Keine Fotos",
    noPhotosHint: "Aufgenommene Fotos erscheinen hier. Sie werden nur auf Ihrem Gerät gespeichert.",
    loading: "Lädt…",
    deleteAll: "Alle",
    deleteAllConfirm: (n) => `Wirklich alle ${n} Fotos löschen?`,
    delete: "Löschen",
    done: "Fertig",
    adjustments: "Anpassungen",
    brightness: "Helligkeit",
    contrast: "Kontrast",
    saturation: "Sättigung",
    warmth: "Wärme",
    presetOriginal: "Original",
    presetBw: "S/W",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Eigen",
    autoSave: "Auto-Save",
    autoSaveOn: "Auto-Save: an",
    autoSaveOff: "Auto-Save: aus",
    autoShare: "Teilen",
    autoShareOn: "Auto-Teilen: an",
    autoShareOff: "Auto-Teilen: aus",
    savedToFolder: (name) => `Gespeichert in ${name}`,
    savedToDownloads: "Gespeichert in Downloads (RealSelfie_…)",
    pickFolderPrompt: "Ordner für Fotos wählen",
    folderPickFailed: "Ordner nicht verfügbar. Downloads werden verwendet.",
    hintTitle: "Wo finde ich meine Fotos?",
    hintIosBody: "Fotos werden automatisch als RealSelfie_*.jpg gespeichert.\n\nAuf dem iPhone:\n• Öffnen Sie die App Dateien\n• Durchsuchen → Auf meinem iPhone → Downloads\n• (oder iCloud Drive → Downloads bei iCloud)\n\nHinweis: iOS-Browser können nicht direkt in Fotos schreiben.",
    hintAndroidBody: "Fotos werden automatisch als RealSelfie_*.jpg gespeichert.\n\nAuf Android:\n• Öffnen Sie die App Dateien (oder Meine Dateien)\n• Interner Speicher → Download\n• Suchen Sie Dateien mit RealSelfie_",
    hintDesktopBody: "Fotos werden automatisch als RealSelfie_*.jpg gespeichert.\n\nIn diesem Browser können Sie einen Ordner wählen, die App speichert dort lautlos. Sonst landen Dateien in Downloads.",
    hintGalleryTip: "Tippen Sie auf das 3-Punkte-Menü unten rechts → Galerie — alle Fotos sind in der App verfügbar.",
    hintShareTip: "Tippen Sie nach der Aufnahme auf Teilen — das Systemmenü kann direkt in Fotos / Galerie speichern.",
    hintDesktopFolderTip: "Tipp: Auto-Save aktivieren und einmal einen RealSelfie-Ordner wählen.",
    gotIt: "Verstanden",
    whereSaved: "Wo sind meine Fotos?",
    paywallTitle: "Real Selfie freischalten",
    paywallSubtitle: (n) => `Sie haben Ihre ${n} kostenlosen Fotos verwendet. Erhalten Sie unbegrenzte Aufnahmen + alles unten für eine kleine Zahlung.`,
    paywallBenefitUnlimited: "Unbegrenzte Fotos für immer",
    paywallBenefitFilters: "Alle Filter freigeschaltet",
    paywallBenefitAspect: "Alle Seitenverhältnisse + Front-Blitz",
    paywallBenefitFuture: "Alle zukünftigen Premium-Features",
    paywallCta: "Für 1,99 € freischalten",
    paywallSkip: "Vielleicht später",
    paywallSecureNote: "Sichere Zahlung über Stripe. Einmalig, kein Abo.",
    paymentPending: "Zahlung wird verarbeitet…",
    premiumUnlocked: "Premium freigeschaltet!",
    paymentFailed: "Zahlung nicht abgeschlossen",
    freeLeft: (n) => (n === 1 ? "1 Gratis-Foto übrig" : `${n} Gratis-Fotos übrig`),
    freeUsed: "Testversion aufgebraucht",
    premiumBadge: "Premium",
    restorePremium: "Premium wiederherstellen",
    restoreTitle: "Schon bezahlt? Wiederherstellen",
    restoreDescription: "Gib die E-Mail-Adresse aus dem Checkout ein. Premium wird auf diesem Gerät freigeschaltet.",
    restoreEmailPlaceholder: "dein@email.de",
    restoreSubmit: "Premium wiederherstellen",
    restoreSuccess: "Premium wiederhergestellt! 🎉",
    restoreNotFound: "Mit dieser E-Mail wurde keine Zahlung gefunden.",
    restoreError: "Wiederherstellung fehlgeschlagen. Bitte erneut versuchen.",
  },
  es: {
    live: "Live · Real",
    off: "Off",
    timerOff: "Temporizador apagado",
    timer: (s) => `Temporizador: ${s}s`,
    aspect: (label) => `Proporción: ${label}`,
    flashOn: "Flash: encendido",
    flashOff: "Flash: apagado",
    previewMirrored: "Vista espejada · Foto real",
    previewReal: "Vista · Imagen real",
    savedToGallery: "Guardado en galería",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Repetir",
    share: "Compartir",
    download: "Descargar",
    photoSaved: "Foto guardada",
    shareFail: "Error al compartir",
    shareUnsupported: "Tu dispositivo no admite compartir nativo",
    photoDeleted: "Foto eliminada",
    galleryCleared: "Galería vaciada",
    cameraUnavailable: "Cámara no disponible",
    permissionDenied: "Acceso a la cámara denegado. Permítelo en los ajustes del navegador.",
    noCamera: "No se encontró ninguna cámara.",
    cameraBusy: "La cámara está siendo usada por otra app.",
    cameraGeneric: "No se pudo abrir la cámara.",
    tryAgain: "Reintentar",
    gallery: "Galería",
    photos: (n) => (n === 1 ? "1 foto" : `${n} fotos`),
    noPhotos: "No hay fotos",
    noPhotosHint: "Las fotos capturadas aparecerán aquí. Solo se guardan en tu dispositivo.",
    loading: "Cargando…",
    deleteAll: "Todo",
    deleteAllConfirm: (n) => `¿Eliminar las ${n} fotos?`,
    delete: "Eliminar",
    done: "Listo",
    adjustments: "Ajustes",
    brightness: "Brillo",
    contrast: "Contraste",
    saturation: "Saturación",
    warmth: "Calidez",
    presetOriginal: "Original",
    presetBw: "B/N",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Personal",
    autoSave: "Auto-guardar",
    autoSaveOn: "Auto-guardar: activado",
    autoSaveOff: "Auto-guardar: desactivado",
    autoShare: "Compartir",
    autoShareOn: "Auto-compartir: activado",
    autoShareOff: "Auto-compartir: desactivado",
    savedToFolder: (name) => `Guardado en ${name}`,
    savedToDownloads: "Guardado en Descargas (RealSelfie_…)",
    pickFolderPrompt: "Elige carpeta para las fotos",
    folderPickFailed: "Carpeta no disponible. Se usará Descargas.",
    hintTitle: "¿Dónde están mis fotos?",
    hintIosBody: "Las fotos se guardan automáticamente como RealSelfie_*.jpg.\n\nEn iPhone:\n• Abre la app Archivos\n• Explorar → En mi iPhone → Descargas\n• (o iCloud Drive → Descargas si usas iCloud)\n\nNota: los navegadores de iOS no pueden escribir directamente en Fotos.",
    hintAndroidBody: "Las fotos se guardan automáticamente como RealSelfie_*.jpg.\n\nEn Android:\n• Abre la app Archivos (o Mis Archivos)\n• Almacenamiento interno → Download\n• Busca archivos que empiecen con RealSelfie_",
    hintDesktopBody: "Las fotos se guardan automáticamente como RealSelfie_*.jpg.\n\nEn este navegador puedes elegir una carpeta y la app guardará allí silenciosamente. Si no, irán a Descargas.",
    hintGalleryTip: "Toca el menú 3 puntos abajo a la derecha → Galería — todas tus fotos están siempre en la app.",
    hintShareTip: "Después de tomar la foto, toca Compartir — el menú del sistema puede guardar directamente en Fotos / Galería.",
    hintDesktopFolderTip: "Consejo: activa Auto-guardar y elige una carpeta RealSelfie una vez.",
    gotIt: "Entendido",
    whereSaved: "¿Dónde están mis fotos?",
    paywallTitle: "Desbloquear Real Selfie",
    paywallSubtitle: (n) => `Has usado tus ${n} fotos gratis. Obtén capturas ilimitadas + todo lo siguiente por un pequeño pago.`,
    paywallBenefitUnlimited: "Fotos ilimitadas para siempre",
    paywallBenefitFilters: "Todos los filtros desbloqueados",
    paywallBenefitAspect: "Todos los formatos + flash frontal",
    paywallBenefitFuture: "Todas las futuras funciones Premium",
    paywallCta: "Desbloquear por 1,99 €",
    paywallSkip: "Más tarde",
    paywallSecureNote: "Pago seguro vía Stripe. Pago único, sin suscripción.",
    paymentPending: "Procesando pago…",
    premiumUnlocked: "¡Premium desbloqueado!",
    paymentFailed: "El pago no se completó",
    freeLeft: (n) => (n === 1 ? "Última foto gratis" : `${n} fotos gratis restantes`),
    freeUsed: "Prueba gratuita agotada",
    premiumBadge: "Premium",
    restorePremium: "Restaurar Premium",
    restoreTitle: "¿Ya pagaste? Restaurar",
    restoreDescription: "Introduce el correo usado al pagar. Restauraremos Premium en este dispositivo.",
    restoreEmailPlaceholder: "tu@email.es",
    restoreSubmit: "Restaurar Premium",
    restoreSuccess: "¡Premium restaurado! 🎉",
    restoreNotFound: "No encontramos un pago con este correo.",
    restoreError: "Falló la restauración. Inténtalo de nuevo.",
  },
  fr: {
    live: "Live · Réel",
    off: "Off",
    timerOff: "Minuteur désactivé",
    timer: (s) => `Minuteur : ${s}s`,
    aspect: (label) => `Format : ${label}`,
    flashOn: "Flash : activé",
    flashOff: "Flash : désactivé",
    previewMirrored: "Aperçu miroir · Photo réelle",
    previewReal: "Aperçu · Image réelle",
    savedToGallery: "Enregistré dans la galerie",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Reprendre",
    share: "Partager",
    download: "Télécharger",
    photoSaved: "Photo enregistrée",
    shareFail: "Échec du partage",
    shareUnsupported: "Votre appareil ne prend pas en charge le partage natif",
    photoDeleted: "Photo supprimée",
    galleryCleared: "Galerie vidée",
    cameraUnavailable: "Caméra indisponible",
    permissionDenied: "Accès à la caméra refusé. Autorisez-le dans les paramètres du navigateur.",
    noCamera: "Aucune caméra trouvée.",
    cameraBusy: "La caméra est utilisée par une autre application.",
    cameraGeneric: "Impossible d'ouvrir la caméra.",
    tryAgain: "Réessayer",
    gallery: "Galerie",
    photos: (n) => (n === 1 ? "1 photo" : `${n} photos`),
    noPhotos: "Aucune photo",
    noPhotosHint: "Les photos prises apparaîtront ici. Elles sont stockées uniquement sur votre appareil.",
    loading: "Chargement…",
    deleteAll: "Tout",
    deleteAllConfirm: (n) => `Vraiment supprimer les ${n} photos ?`,
    delete: "Supprimer",
    done: "OK",
    adjustments: "Réglages",
    brightness: "Luminosité",
    contrast: "Contraste",
    saturation: "Saturation",
    warmth: "Chaleur",
    presetOriginal: "Original",
    presetBw: "N/B",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Perso",
    autoSave: "Auto-enreg.",
    autoSaveOn: "Auto-enregistrement : activé",
    autoSaveOff: "Auto-enregistrement : désactivé",
    autoShare: "Partager",
    autoShareOn: "Auto-partage : activé",
    autoShareOff: "Auto-partage : désactivé",
    savedToFolder: (name) => `Enregistré dans ${name}`,
    savedToDownloads: "Enregistré dans Téléchargements (RealSelfie_…)",
    pickFolderPrompt: "Choisir un dossier pour les photos",
    folderPickFailed: "Dossier indisponible. Téléchargements utilisés.",
    hintTitle: "Où sont mes photos ?",
    hintIosBody: "Les photos sont enregistrées automatiquement sous RealSelfie_*.jpg.\n\nSur iPhone :\n• Ouvrez l'app Fichiers\n• Parcourir → Sur mon iPhone → Téléchargements\n• (ou iCloud Drive → Téléchargements)\n\nNote : les navigateurs iOS ne peuvent pas écrire directement dans Photos.",
    hintAndroidBody: "Les photos sont enregistrées automatiquement sous RealSelfie_*.jpg.\n\nSur Android :\n• Ouvrez l'app Fichiers (ou Mes fichiers)\n• Stockage interne → Download\n• Cherchez les fichiers commençant par RealSelfie_",
    hintDesktopBody: "Les photos sont enregistrées automatiquement sous RealSelfie_*.jpg.\n\nDans ce navigateur, vous pouvez choisir un dossier et l'app y écrira silencieusement. Sinon les fichiers vont dans Téléchargements.",
    hintGalleryTip: "Touchez le menu 3 points en bas à droite → Galerie — toutes vos photos sont toujours dans l'app.",
    hintShareTip: "Après la photo, touchez Partager — le menu système peut enregistrer directement dans Photos / Galerie.",
    hintDesktopFolderTip: "Astuce : activez Auto-enreg. et choisissez un dossier RealSelfie une fois.",
    gotIt: "OK",
    whereSaved: "Où sont mes photos ?",
    paywallTitle: "Débloquer Real Selfie",
    paywallSubtitle: (n) => `Vous avez utilisé vos ${n} photos gratuites. Obtenez des captures illimitées + tout ce qui suit pour un petit paiement.`,
    paywallBenefitUnlimited: "Photos illimitées pour toujours",
    paywallBenefitFilters: "Tous les filtres débloqués",
    paywallBenefitAspect: "Tous les formats + flash frontal",
    paywallBenefitFuture: "Toutes les futures fonctions Premium",
    paywallCta: "Débloquer pour 1,99 €",
    paywallSkip: "Plus tard",
    paywallSecureNote: "Paiement sécurisé par Stripe. Unique, sans abonnement.",
    paymentPending: "Traitement du paiement…",
    premiumUnlocked: "Premium débloqué !",
    paymentFailed: "Le paiement n'a pas abouti",
    freeLeft: (n) => (n === 1 ? "Dernière photo gratuite" : `${n} photos gratuites restantes`),
    freeUsed: "Essai gratuit épuisé",
    premiumBadge: "Premium",
    restorePremium: "Restaurer Premium",
    restoreTitle: "Déjà payé ? Restaurer",
    restoreDescription: "Saisissez l'e-mail utilisé lors du paiement. Premium sera réactivé sur cet appareil.",
    restoreEmailPlaceholder: "votre@email.fr",
    restoreSubmit: "Restaurer Premium",
    restoreSuccess: "Premium restauré ! 🎉",
    restoreNotFound: "Aucun paiement trouvé avec cet e-mail.",
    restoreError: "Échec de la restauration. Réessayez.",
  },
  it: {
    live: "Live · Reale",
    off: "Off",
    timerOff: "Timer disattivato",
    timer: (s) => `Timer: ${s}s`,
    aspect: (label) => `Proporzioni: ${label}`,
    flashOn: "Flash: acceso",
    flashOff: "Flash: spento",
    previewMirrored: "Anteprima specchiata · Foto reale",
    previewReal: "Anteprima · Immagine reale",
    savedToGallery: "Salvato in galleria",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Rifai",
    share: "Condividi",
    download: "Scarica",
    photoSaved: "Foto salvata",
    shareFail: "Condivisione non riuscita",
    shareUnsupported: "Il tuo dispositivo non supporta la condivisione nativa",
    photoDeleted: "Foto eliminata",
    galleryCleared: "Galleria svuotata",
    cameraUnavailable: "Fotocamera non disponibile",
    permissionDenied: "Accesso alla fotocamera negato. Consentilo nelle impostazioni del browser.",
    noCamera: "Nessuna fotocamera trovata.",
    cameraBusy: "La fotocamera è in uso da un'altra app.",
    cameraGeneric: "Impossibile aprire la fotocamera.",
    tryAgain: "Riprova",
    gallery: "Galleria",
    photos: (n) => (n === 1 ? "1 foto" : `${n} foto`),
    noPhotos: "Nessuna foto",
    noPhotosHint: "Le foto scattate appariranno qui. Sono memorizzate solo sul tuo dispositivo.",
    loading: "Caricamento…",
    deleteAll: "Tutto",
    deleteAllConfirm: (n) => `Eliminare davvero tutte le ${n} foto?`,
    delete: "Elimina",
    done: "Fatto",
    adjustments: "Regolazioni",
    brightness: "Luminosità",
    contrast: "Contrasto",
    saturation: "Saturazione",
    warmth: "Calore",
    presetOriginal: "Originale",
    presetBw: "B/N",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Pers.",
    autoSave: "Auto-salva",
    autoSaveOn: "Auto-salvataggio: attivo",
    autoSaveOff: "Auto-salvataggio: disattivato",
    autoShare: "Condividi",
    autoShareOn: "Auto-condivisione: attiva",
    autoShareOff: "Auto-condivisione: disattivata",
    savedToFolder: (name) => `Salvato in ${name}`,
    savedToDownloads: "Salvato in Download (RealSelfie_…)",
    pickFolderPrompt: "Scegli cartella per le foto",
    folderPickFailed: "Cartella non disponibile. Verrà usato Download.",
    hintTitle: "Dove sono le mie foto?",
    hintIosBody: "Le foto vengono salvate automaticamente come RealSelfie_*.jpg.\n\nSu iPhone:\n• Apri l'app File\n• Sfoglia → Sul mio iPhone → Download\n• (o iCloud Drive → Download se usi iCloud)\n\nNota: i browser iOS non possono scrivere direttamente nell'app Foto.",
    hintAndroidBody: "Le foto vengono salvate automaticamente come RealSelfie_*.jpg.\n\nSu Android:\n• Apri l'app File (o I miei file)\n• Memoria interna → Download\n• Cerca file che iniziano con RealSelfie_",
    hintDesktopBody: "Le foto vengono salvate automaticamente come RealSelfie_*.jpg.\n\nIn questo browser puoi scegliere una cartella e l'app scriverà lì in silenzio. Altrimenti i file vanno in Download.",
    hintGalleryTip: "Tocca il menu 3 punti in basso a destra → Galleria — tutte le tue foto sono sempre nell'app.",
    hintShareTip: "Dopo lo scatto, tocca Condividi — il menu di sistema può salvare direttamente in Foto / Galleria.",
    hintDesktopFolderTip: "Suggerimento: attiva Auto-salva e scegli una cartella RealSelfie una volta.",
    gotIt: "OK",
    whereSaved: "Dove sono le mie foto?",
    paywallTitle: "Sblocca Real Selfie",
    paywallSubtitle: (n) => `Hai usato le tue ${n} foto gratuite. Ottieni scatti illimitati + tutto sotto con un piccolo pagamento.`,
    paywallBenefitUnlimited: "Foto illimitate per sempre",
    paywallBenefitFilters: "Tutti i filtri sbloccati",
    paywallBenefitAspect: "Tutti i formati + flash frontale",
    paywallBenefitFuture: "Tutte le future funzioni Premium",
    paywallCta: "Sblocca per 1,99 €",
    paywallSkip: "Forse più tardi",
    paywallSecureNote: "Pagamento sicuro con Stripe. Una tantum, niente abbonamento.",
    paymentPending: "Elaboro pagamento…",
    premiumUnlocked: "Premium sbloccato!",
    paymentFailed: "Pagamento non completato",
    freeLeft: (n) => (n === 1 ? "Ultima foto gratis" : `${n} foto gratis rimaste`),
    freeUsed: "Prova gratuita esaurita",
    premiumBadge: "Premium",
    restorePremium: "Ripristina Premium",
    restoreTitle: "Già pagato? Ripristina",
    restoreDescription: "Inserisci l'email usata al checkout. Premium verrà sbloccato su questo dispositivo.",
    restoreEmailPlaceholder: "tua@email.it",
    restoreSubmit: "Ripristina Premium",
    restoreSuccess: "Premium ripristinato! 🎉",
    restoreNotFound: "Nessun pagamento trovato con questa email.",
    restoreError: "Ripristino non riuscito. Riprova.",
  },
  pl: {
    live: "Live · Realnie",
    off: "Off",
    timerOff: "Timer wyłączony",
    timer: (s) => `Timer: ${s}s`,
    aspect: (label) => `Proporcje: ${label}`,
    flashOn: "Lampa: włączona",
    flashOff: "Lampa: wyłączona",
    previewMirrored: "Podgląd lustrzany · Realne zdjęcie",
    previewReal: "Podgląd · Realny obraz",
    savedToGallery: "Zapisano w galerii",
    realSelfie: "Real Selfie",
    on: "On",
    retake: "Powtórz",
    share: "Udostępnij",
    download: "Pobierz",
    photoSaved: "Zdjęcie zapisane",
    shareFail: "Udostępnianie nie powiodło się",
    shareUnsupported: "Twoje urządzenie nie obsługuje natywnego udostępniania",
    photoDeleted: "Zdjęcie usunięte",
    galleryCleared: "Galeria wyczyszczona",
    cameraUnavailable: "Kamera niedostępna",
    permissionDenied: "Dostęp do kamery odrzucony. Zezwól w ustawieniach przeglądarki.",
    noCamera: "Nie znaleziono kamery.",
    cameraBusy: "Kamera jest używana przez inną aplikację.",
    cameraGeneric: "Nie udało się otworzyć kamery.",
    tryAgain: "Spróbuj ponownie",
    gallery: "Galeria",
    photos: (n) => (n === 1 ? "1 zdjęcie" : n >= 2 && n <= 4 ? `${n} zdjęcia` : `${n} zdjęć`),
    noPhotos: "Brak zdjęć",
    noPhotosHint: "Wykonane zdjęcia pojawią się tutaj. Są zapisywane tylko na Twoim urządzeniu.",
    loading: "Ładowanie…",
    deleteAll: "Wszystko",
    deleteAllConfirm: (n) => `Na pewno usunąć wszystkie ${n} zdjęć?`,
    delete: "Usuń",
    done: "Gotowe",
    adjustments: "Regulacje",
    brightness: "Jasność",
    contrast: "Kontrast",
    saturation: "Nasycenie",
    warmth: "Temperatura",
    presetOriginal: "Oryginał",
    presetBw: "Cz/B",
    presetVintage: "Vintage",
    presetSoft: "Soft",
    presetCinema: "Cinema",
    presetCustom: "Własne",
    autoSave: "Auto-zapis",
    autoSaveOn: "Auto-zapis: włączony",
    autoSaveOff: "Auto-zapis: wyłączony",
    autoShare: "Udostępnij",
    autoShareOn: "Auto-udostępnianie: włączone",
    autoShareOff: "Auto-udostępnianie: wyłączone",
    savedToFolder: (name) => `Zapisano w ${name}`,
    savedToDownloads: "Zapisano w Pobranych (RealSelfie_…)",
    pickFolderPrompt: "Wybierz folder dla zdjęć",
    folderPickFailed: "Folder niedostępny. Użyte zostaną Pobrane.",
    hintTitle: "Gdzie są moje zdjęcia?",
    hintIosBody: "Zdjęcia zapisują się automatycznie jako RealSelfie_*.jpg.\n\nNa iPhone:\n• Otwórz aplikację Pliki\n• Przeglądaj → Na moim iPhonie → Pobrane\n• (lub iCloud Drive → Pobrane jeśli używasz iCloud)\n\nUwaga: przeglądarki iOS nie mogą zapisywać bezpośrednio do Zdjęć.",
    hintAndroidBody: "Zdjęcia zapisują się automatycznie jako RealSelfie_*.jpg.\n\nNa Androidzie:\n• Otwórz aplikację Pliki (lub Moje pliki)\n• Pamięć wewnętrzna → Download\n• Szukaj plików zaczynających się od RealSelfie_",
    hintDesktopBody: "Zdjęcia zapisują się automatycznie jako RealSelfie_*.jpg.\n\nW tej przeglądarce możesz wybrać folder, a aplikacja będzie tam zapisywać po cichu. W innym razie pliki trafiają do Pobranych.",
    hintGalleryTip: "Kliknij menu 3 kropki w prawym dolnym rogu → Galeria — wszystkie zdjęcia są zawsze w aplikacji.",
    hintShareTip: "Po zrobieniu zdjęcia kliknij Udostępnij — menu systemu zapisze bezpośrednio w Zdjęciach / Galerii.",
    hintDesktopFolderTip: "Wskazówka: włącz Auto-zapis i wybierz folder RealSelfie raz.",
    gotIt: "Rozumiem",
    whereSaved: "Gdzie są moje zdjęcia?",
    paywallTitle: "Odblokuj Real Selfie",
    paywallSubtitle: (n) => `Wykorzystałeś swoje ${n} darmowych zdjęć. Zdobądź nieograniczone zdjęcia + wszystko poniżej za jedną małą opłatę.`,
    paywallBenefitUnlimited: "Nieograniczone zdjęcia na zawsze",
    paywallBenefitFilters: "Wszystkie filtry odblokowane",
    paywallBenefitAspect: "Wszystkie proporcje + przedni flesz",
    paywallBenefitFuture: "Wszystkie przyszłe funkcje Premium",
    paywallCta: "Odblokuj za 1,99 €",
    paywallSkip: "Może później",
    paywallSecureNote: "Bezpieczna płatność przez Stripe. Jednorazowa, bez subskrypcji.",
    paymentPending: "Przetwarzam płatność…",
    premiumUnlocked: "Premium odblokowane!",
    paymentFailed: "Płatność nie została ukończona",
    freeLeft: (n) => (n === 1 ? "Ostatnie darmowe zdjęcie" : `${n} darmowych zdjęć zostało`),
    freeUsed: "Wersja próbna wykorzystana",
    premiumBadge: "Premium",
    restorePremium: "Przywróć Premium",
    restoreTitle: "Już zapłaciłeś? Przywróć",
    restoreDescription: "Podaj e-mail użyty przy płatności. Premium zostanie przywrócone na tym urządzeniu.",
    restoreEmailPlaceholder: "twoj@email.pl",
    restoreSubmit: "Przywróć Premium",
    restoreSuccess: "Premium przywrócone! 🎉",
    restoreNotFound: "Nie znaleziono płatności z tym e-mailem.",
    restoreError: "Przywracanie nie powiodło się. Spróbuj ponownie.",
  },
};

const SUPPORTED = Object.keys(dict);

function detectLocale() {
  const langs = navigator.languages || [navigator.language || "en"];
  for (const full of langs) {
    if (!full) continue;
    const base = full.toLowerCase().split("-")[0];
    if (SUPPORTED.includes(base)) return base;
  }
  return "en";
}

const LOCALE = detectLocale();

export function t(key, ...args) {
  const entry = dict[LOCALE]?.[key] ?? dict.en[key];
  if (typeof entry === "function") return entry(...args);
  return entry ?? key;
}

export function getLocale() {
  return LOCALE;
}
